import { useState } from "react";
import {
  Apple,
  BellRing,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  History,
  ClipboardList,
  ListChecks,
  Mail,
  Smartphone,
  Sparkles,
  UserCheck,
} from "lucide-react";
import {
  createEmailComposeUrl,
  NexusFooter,
  NexusPublicNav,
  useContactEmail,
} from "./PublicSiteComponents";
import { landingMobileScreenshot, mobilePageScreenshots } from "./mobileScreenshots";
import "./LandingPage.css";

const mobileModules = [
  {
    icon: CalendarDays,
    title: "Schedule Viewing",
    copy: "Members can see upcoming events, operating windows, and important organization updates from one mobile surface.",
  },
  {
    icon: ClipboardList,
    title: "RSVP Flow",
    copy: "Collect member intent before an event starts and give operators cleaner visibility into expected attendance.",
  },
  {
    icon: ListChecks,
    title: "Waitlist Requests",
    copy: "Let members request placement from their phone so your staff can spend less time sorting manual messages.",
  },
  {
    icon: BellRing,
    title: "Member Updates",
    copy: "Keep members aligned with timely alerts, status changes, and community notifications as the app expands.",
  },
  {
    icon: UserCheck,
    title: "Staff Mobile Login",
    copy: "Employees and staff can access a dedicated mobile workflow for viewing assigned schedules, availability, and shift opportunities.",
  },
  {
    icon: History,
    title: "Staff History",
    copy: "Give team members visibility into their performance records, activity history, and past shift context from one connected profile.",
  },
];

const roadmapItems = [
  "Optional member-facing mobile access",
  "RSVPs, waitlists, and schedule viewing",
  "Rewards and promotional tracking",
  "Cleaner handoff between admins, staff, and members",
];

export default function MobilePage() {
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const { contactEmail } = useContactEmail();
  const mobileInquiryUrl = createEmailComposeUrl({
    email: contactEmail,
    subject: "Nexus Mobile inquiry",
  });
  const screenshots = mobilePageScreenshots;
  const activeItem = screenshots[activeScreenshot];
  const previousItem =
    screenshots[
      activeScreenshot === 0 ? screenshots.length - 1 : activeScreenshot - 1
    ];
  const nextItem =
    screenshots[
      activeScreenshot === screenshots.length - 1 ? 0 : activeScreenshot + 1
    ];

  const showPreviousScreenshot = () => {
    setActiveScreenshot((current) =>
      current === 0 ? screenshots.length - 1 : current - 1,
    );
  };

  const showNextScreenshot = () => {
    setActiveScreenshot((current) =>
      current === screenshots.length - 1 ? 0 : current + 1,
    );
  };

  const handleTouchEnd = (event) => {
    if (touchStartX === null) return;

    const deltaX = touchStartX - event.changedTouches[0].clientX;
    if (Math.abs(deltaX) > 40) {
      if (deltaX > 0) showNextScreenshot();
      else showPreviousScreenshot();
    }

    setTouchStartX(null);
  };

  return (
    <main className="nexus-home">
      <NexusPublicNav />

      <section className="nexus-page-hero" aria-labelledby="mobile-page-title">
        <div className="nexus-page-hero__copy">
          <p className="nexus-eyebrow">Nexus Mobile App</p>
          <h1 id="mobile-page-title">A dedicated member experience for modern clubs</h1>
          <p>
            Nexus Mobile gives members a smoother way to view schedules, send
            RSVPs, request waitlist placement, and stay connected to the
            organization without constant manual coordination.
          </p>
          <div className="nexus-hero__actions">
            <button className="nexus-store-badge" type="button" disabled>
              <Apple size={20} aria-hidden="true" />
              <span>
                Coming Soon
                <strong>App Store</strong>
              </span>
            </button>
            <a
              className="nexus-button nexus-button--ghost"
              href={mobileInquiryUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!mobileInquiryUrl}
              onClick={(event) => {
                if (!mobileInquiryUrl) event.preventDefault();
              }}
            >
              <Mail size={18} aria-hidden="true" />
              Ask About Mobile
            </a>
          </div>
        </div>
        <div className="nexus-phone nexus-phone--page" aria-label="Nexus Mobile welcome screen preview">
          <img
            className="nexus-phone__image"
            src={landingMobileScreenshot.src}
            alt={landingMobileScreenshot.alt}
          />
        </div>
      </section>

      <section className="nexus-section nexus-screenshot-tour" aria-labelledby="mobile-tour-title">
        <div className="nexus-section__heading nexus-section__heading--split">
          <div>
            <p className="nexus-eyebrow">App tour</p>
            <h2 id="mobile-tour-title">Swipe through the member experience</h2>
          </div>
        </div>
        <div className="nexus-screenshot-tour__layout" aria-live="polite">
          <button
            className="nexus-screenshot-tour__nav nexus-screenshot-tour__nav--previous"
            type="button"
            onClick={showPreviousScreenshot}
            aria-label="Previous screenshot"
          >
            <ChevronLeft size={22} aria-hidden="true" />
          </button>
          <div
            className="nexus-screenshot-tour__stage"
            onTouchStart={(event) => setTouchStartX(event.touches[0].clientX)}
            onTouchEnd={handleTouchEnd}
          >
            <img
              className="nexus-screenshot-tour__peek nexus-screenshot-tour__peek--left"
              src={previousItem.src}
              alt=""
              aria-hidden="true"
            />
            <img
              className="nexus-screenshot-tour__active"
              src={activeItem.src}
              alt={activeItem.alt}
            />
            <img
              className="nexus-screenshot-tour__peek nexus-screenshot-tour__peek--right"
              src={nextItem.src}
              alt=""
              aria-hidden="true"
            />
          </div>
          <button
            className="nexus-screenshot-tour__nav nexus-screenshot-tour__nav--next"
            type="button"
            onClick={showNextScreenshot}
            aria-label="Next screenshot"
          >
            <ChevronRight size={22} aria-hidden="true" />
          </button>
        </div>
        <div className="nexus-screenshot-tour__dots" aria-label="Choose a screenshot">
          {screenshots.map((item, index) => (
            <button
              type="button"
              className={index === activeScreenshot ? "is-selected" : ""}
              onClick={() => setActiveScreenshot(index)}
              key={item.label}
              aria-label={`Show ${item.label} screenshot`}
            />
          ))}
        </div>
      </section>

      <section className="nexus-section nexus-section--systems">
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">Member layer</p>
          <h2>Mobile workflows built around cleaner operations</h2>
        </div>
        <div className="nexus-systems">
          {mobileModules.map(({ icon: Icon, title, copy }) => (
            <article key={title}>
              <Icon size={22} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="nexus-section nexus-mobile">
        <div className="nexus-mobile__layout">
          <div className="nexus-mobile__copy">
            <Sparkles size={26} aria-hidden="true" />
            <h3>Optional for members, powerful for operators.</h3>
            <p>
              Members do not have to download the app to participate in your
              organization. For clubs that want smoother RSVPs, fewer manual
              messages, cleaner schedule visibility, and stronger member
              engagement, Nexus Mobile becomes the fastest path.
            </p>
          </div>
          <div className="nexus-mobile__features">
            {roadmapItems.map((feature) => (
              <span key={feature}>
                <Smartphone size={17} aria-hidden="true" />
                {feature}
              </span>
            ))}
          </div>
        </div>
      </section>

      <NexusFooter />
    </main>
  );
}
